import React, { useEffect, useState } from "react";
import { X, Download, Loader2, FileImage, FileText, Layers } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";

/**
 * UploadHistoryDialog — lists every file the user has uploaded for this
 * design (source_files array on the design doc) with a download link.
 *
 * Opens when the parent sets `open`. Fetches lazily so the API call
 * doesn't fire on Editor mount.
 */
export default function UploadHistoryDialog({ open, designId, onClose }) {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (!open || !designId) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/designs/${designId}/source-files`);
        if (!cancel) setFiles(r.data?.files || []);
      } catch (err) {
        if (!cancel) toast.error(err?.response?.data?.detail || "Could not load upload history");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [open, designId]);

  const downloadFile = (index, filename) => {
    // Open in a new tab; the endpoint sets Content-Disposition inline
    // so the browser will preview or download depending on the mime.
    const base = (API || "").replace(/\/$/, "");
    const url = `${base}/designs/${designId}/source-files/${index}`;
    // We use the axios instance so the session_token cookie is included
    // in the request. Then serve as a Blob URL for download.
    api.get(`/designs/${designId}/source-files/${index}`, { responseType: "blob" })
      .then((r) => {
        const blob = new Blob([r.data], { type: r.headers["content-type"] || "application/octet-stream" });
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objUrl;
        a.download = filename || "download.bin";
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(objUrl), 4000);
      })
      .catch(() => {
        toast.error("Download failed — try opening the direct link");
        window.open(url, "_blank"); // fallback
      });
  };

  if (!open) return null;
  return (
    <div
      data-testid="upload-history-dialog"
      className="fixed inset-0 z-50 grid place-items-center bg-zinc-950/80 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-lg bg-zinc-950 border border-brass-500/40">
        <header className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400 mb-1">
              // Upload History
            </div>
            <h2 className="font-display text-xl text-zinc-50">Source files</h2>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100 p-1" data-testid="upload-history-close">
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="p-6 space-y-2 max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="flex items-center gap-2 text-zinc-500 font-mono text-xs">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {!loading && files.length === 0 && (
            <div className="font-body text-sm text-zinc-500 text-center py-8">
              No files uploaded for this design yet.
              <br />
              Drop a floor-plan sketch into the "Upload Sketch" flow to see it here.
            </div>
          )}
          {!loading && files.map((f) => (
            <div
              key={f.index}
              className="flex items-center gap-3 border border-zinc-800 hover:border-brass-500/40 px-4 py-3 group transition-colors"
              data-testid={`upload-history-item-${f.index}`}
            >
              {f.thumbnail_data_url ? (
                <img
                  src={f.thumbnail_data_url}
                  alt={f.filename || "preview"}
                  className="w-12 h-12 object-cover border border-brass-700/40 shrink-0"
                  data-testid={`upload-history-thumb-${f.index}`}
                />
              ) : (
                <div className="w-12 h-12 grid place-items-center border border-zinc-800 bg-zinc-900 shrink-0">
                  <FileIcon mime={f.mime} />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-body text-sm text-zinc-100 truncate">{f.filename || "unnamed"}</div>
                <div className="font-mono text-[10px] text-zinc-500">
                  {(f.mime || "unknown")} · {formatBytes(f.size || 0)} · {formatDate(f.uploaded_at)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => downloadFile(f.index, f.filename)}
                className="opacity-60 group-hover:opacity-100 border border-brass-700/60 bg-brass-950/20 hover:bg-brass-900/40 text-brass-200 font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 inline-flex items-center gap-1.5 transition"
                data-testid={`upload-history-download-${f.index}`}
              >
                <Download className="w-3 h-3" />
                Download
              </button>
            </div>
          ))}
        </div>
        <footer className="flex items-center justify-end gap-2 px-6 py-4 border-t border-zinc-800 bg-zinc-900/40">
          <button
            type="button"
            onClick={onClose}
            className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-zinc-100 px-4 py-2"
            data-testid="upload-history-done"
          >
            Done
          </button>
        </footer>
      </div>
    </div>
  );
}

function FileIcon({ mime }) {
  const m = mime || "";
  if (m.startsWith("image/")) return <FileImage className="w-5 h-5 text-brass-400 shrink-0" />;
  if (m === "application/pdf") return <FileText className="w-5 h-5 text-brass-400 shrink-0" />;
  return <Layers className="w-5 h-5 text-zinc-500 shrink-0" />;
}

function formatBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}
